import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout, { Container } from "../components/layout";
import Pagination from "../components/pagination";
import { SuwasthiLoader } from "../components/loader";
import { FaUsers, FaCheckCircle, FaTimesCircle, FaClock, FaSearch } from "react-icons/fa";
import { apiurl } from "../config/config";
import { getCookie } from "../config/webStorage";
import { useUser } from "../config/userProvider";

const RecruiterApplications = () => {
  const { userData } = useUser();
  const recruiterId = userData?.employeeDetails.employeeId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ------------------- STATE -------------------
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, statuses: {} });
  const [range, setRange] = useState("");
  const [customDates, setCustomDates] = useState({ startDate: "", endDate: "" });

  // ------------------- DATE HELPERS -------------------
  const getDateRange = (type) => {
    const endDate = new Date();
    let startDate = new Date();
    if (type === "7") startDate.setDate(endDate.getDate() - 7);
    else if (type === "30") startDate.setDate(endDate.getDate() - 30);

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  // ------------------- FETCH APPLICATIONS -------------------
  const fetchApplications = async (params = {}) => {
    setLoading(true);
    try {
      const token = getCookie("swtoken");
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        recruiterId,
        ...params,
      }).toString();

      const res = await fetch(`${apiurl}/api/application/application-by-recruiter?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        setApplications(data.applications || []);
        setTotalItems(data.pagination?.totalItems || 0);
        setStats(data.stats || { total: 0, statuses: {} });
      }
    } catch (err) {
      console.error("Error fetching recruiter applications:", err);
    } finally {
      setLoading(false);
    }
  };

  // ------------------- APPLY FILTER -------------------
  const applyFilter = (params = {}) => {
    // only append params that have real values
    const newParams = {};
    if (params.startDate || customDates.startDate) newParams.startDate = params.startDate || customDates.startDate;
    if (params.endDate || customDates.endDate) newParams.endDate = params.endDate || customDates.endDate;
    if (statusFilter) newParams.status = statusFilter;
    setSearchParams(newParams); // update URL
    fetchApplications(newParams);
  };

  // ------------------- QUICK FILTER -------------------
  const handleQuickFilter = (type) => {
    setRange(type);
    setCustomDates({ startDate: "", endDate: "" });
    const { startDate, endDate } = getDateRange(type);
    applyFilter({ startDate, endDate });
  };

  // ------------------- CUSTOM DATE FILTER -------------------
  const handleCustomFilter = () => {
    if (!customDates.startDate || !customDates.endDate) return;
    setRange("custom");
    applyFilter(customDates);
  };

  // ------------------- INITIAL FETCH FROM URL -------------------
  useEffect(() => {
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");
    const statusParam = searchParams.get("status");

    if (startDateParam && endDateParam) setCustomDates({ startDate: startDateParam, endDate: endDateParam });
    if (statusParam) setStatusFilter(statusParam);

    fetchApplications({
      ...(startDateParam && { startDate: startDateParam }),
      ...(endDateParam && { endDate: endDateParam }),
      ...(statusParam && { status: statusParam }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, searchParams]);

  // ------------------- STATUS ICON -------------------
  const getStatusIcon = (status) => {
    if (status.includes("selected")) return <FaCheckCircle />;
    if (status.includes("rejected")) return <FaTimesCircle />;
    if (status.includes("interview")) return <FaClock />;
    return <FaUsers />;
  };

  const listVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.03 } }),
  };

  // ------------------- RENDER -------------------
  return (
    <Layout>
      <Container overflow="overflow-scroll">
        <div className="p-4 bg-gray-50 min-h-screen space-y-6">
          {/* Filters */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Quick Filters */}
              {["today", "7", "30"].map((r) => (
                <button
                  key={r}
                  onClick={() => handleQuickFilter(r)}
                  className={`px-4 py-2 rounded-lg text-sm transition ${
                    range === r ? "bg-black text-white" : "bg-white border hover:bg-gray-100"
                  }`}
                >
                  {r === "today" ? "Today" : `Last ${r} Days`}
                </button>
              ))}

              {/* Custom Dates */}
              <input
                type="date"
                value={customDates.startDate}
                onChange={(e) => setCustomDates({ ...customDates, startDate: e.target.value })}
                className="border p-2 rounded-lg"
              />
              <input
                type="date"
                value={customDates.endDate}
                onChange={(e) => setCustomDates({ ...customDates, endDate: e.target.value })}
                className="border p-2 rounded-lg"
              />

              {/* Search */}
              <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border shadow-sm">
                <FaSearch className="text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by candidate..."
                  className="w-full focus:outline-none text-gray-700"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  applyFilter();
                }}
                className="border px-2 py-2 rounded-lg"
              >
                <option value="">All Status</option>
                {Object.keys(stats.statuses || {}).map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
                            <button onClick={handleCustomFilter} className="bg-blue-600 text-white px-4 py-2 rounded-lg">
                Apply
              </button>
            </div>
          </div>

          {/* Active Filters */}
          <div className="flex flex-wrap gap-2">
            {range && (
              <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">
                Range: {range === "custom" ? `${customDates.startDate} → ${customDates.endDate}` : range}
              </span>
            )}
            {statusFilter && (
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Status: {statusFilter}</span>
            )}
            {search && <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">Search: {search}</span>}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3 hover:shadow-md transition">
              <FaUsers className="text-2xl text-gray-700" />
              <div>
                <p className="text-sm text-gray-500">Total Applications</p>
                <h2 className="text-2xl font-semibold">{stats.total || 0}</h2>
              </div>
            </div>
            {Object.entries(stats.statuses || {}).map(([status, count]) => (
              <div
                key={status}
                className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3 hover:shadow-md transition"
              >
                {getStatusIcon(status)}
                <div>
                  <p className="text-sm text-gray-500">{status.replace(/_/g, " ").toUpperCase()}</p>
                  <h2 className="text-2xl font-semibold">{count}</h2>
                </div>
              </div>
            ))}
          </div>

          {/* Table */}
          {loading ? (
            <SuwasthiLoader />
          ) : (
            <div className="bg-white shadow-md rounded-xl overflow-x-auto p-4">
              {applications.length === 0 ? (
                <p className="text-gray-500 text-center py-10">No applications found.</p>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="p-3 text-left">HRMS ID</th>
                      <th className="p-3 text-left">Candidate Name</th>
                      <th className="p-3 text-left">Email</th>
                      <th className="p-3 text-left">Status</th>
                      <th className="p-3 text-left">Reference</th>
                      <th className="p-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app, idx) => (
                      <motion.tr
                        key={app._id}
                        variants={listVariants}
                        initial="hidden"
                        animate="visible"
                        custom={idx}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="p-3">{app.id}</td>
                        <td className="p-3">
                          {app.candidateSnapshot?.firstName} {app.candidateSnapshot?.lastName}
                        </td>
                        <td className="p-3">{app.candidateSnapshot?.email}</td>
                        <td className="p-3">{app.applicationStatus}</td>
                        <td className="p-3">
                          {app.referenceDetails?.recruiterName
                            ? `${app.referenceDetails.recruiterName} (ID: ${app.referenceDetails.recruiterId})`
                            : "-"}
                        </td>
                        <td className="p-3">
                          <Link
                            to={`/applications/${app._id}`}
                            className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                          >
                            View
                          </Link>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-5 gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border rounded-md px-2 py-1 text-sm"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default RecruiterApplications;